import { Injectable } from '@angular/core'
import { HttpClient } from '@angular/common/http'
import { firstValueFrom, map, Observable } from 'rxjs'

import { environment } from '../../environments/environment'
import {
  Passkey,
  PasskeyListResponse,
  PasskeyRegistrationOptions,
  PasskeyResponse,
} from '../_models/passkey.interface'
import { WebAuthnService } from './webauthn.service'

// Verwaltung der Passkeys (siehe bentomax/hawk#596). Der Login selbst läuft
// über den SessionService.
@Injectable({
  providedIn: 'root'
})
export class PasskeyService {

  private basePath = environment.apiURL

  constructor(private http: HttpClient, private webAuthn: WebAuthnService) {
  }

  // Eigene Passkeys des angemeldeten Nutzers
  public getPasskeys(): Observable<Passkey[]> {
    return this.http.get<PasskeyListResponse>(`${this.basePath}passkeys.json`).pipe(map((data) => data.passkeys))
  }

  // Legt einen Passkey an: Optionen holen, Browser-Dialog, Ergebnis speichern.
  // Fehler des Browsers (z. B. Abbruch) und der API werden durchgereicht.
  public async registerPasskey(name?: string): Promise<Passkey> {
    const options = await firstValueFrom(
      this.http.post<PasskeyRegistrationOptions>(`${this.basePath}passkeys/registration_options.json`, {})
    )
    const credential = await this.webAuthn.startRegistration(options.options)
    const body = { challenge_id: options.challenge_id, name: name?.trim() || undefined, credential }
    const data = await firstValueFrom(this.http.post<PasskeyResponse>(`${this.basePath}passkeys.json`, body))

    return data.passkey
  }

  public renamePasskey(id: number, name: string): Observable<Passkey> {
    return this.http
      .patch<PasskeyResponse>(`${this.basePath}passkeys/${id}.json`, { name })
      .pipe(map((data) => data.passkey))
  }

  public deletePasskey(id: number): Observable<void> {
    return this.http.delete<void>(`${this.basePath}passkeys/${id}.json`)
  }

  // Admin: Passkeys eines anderen Nutzers
  public getUserPasskeys(userId: number): Observable<Passkey[]> {
    return this.http
      .get<PasskeyListResponse>(`${this.basePath}users/${userId}/passkeys.json`)
      .pipe(map((data) => data.passkeys))
  }

  public deleteUserPasskey(userId: number, id: number): Observable<void> {
    return this.http.delete<void>(`${this.basePath}users/${userId}/passkeys/${id}.json`)
  }
}
